import { FlexBox, Icon, IconNames, Text } from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import produce from 'immer';
import {
  ContextControl,
  DashboardContext,
  InstrumentPanel,
} from 'lib-analytics-types';
import isEqual from 'lodash/isEqual';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { DeepWritable } from 'ts-essentials';

import { isOptionalOperator } from '../../utils/filters/isOptionalOperator';
import { AnalyticsFilter, AnalyticsFilterChangeCallback } from '../filter';

import { ContextMultiToggle } from './components/multiToggle/contextMultiToggle';

type AnalyticsContextProps = {
  /** The parent instrument panel, if relevant. */
  panel?: InstrumentPanel;
  /** The context filter definition. */
  context: DashboardContext;
  /** The currently selected context. */
  control?: ContextControl;
  /** The icon to display next to the context label. */
  icon?: IconNames;
  /** Callback for when the context value is changed. */
  onChange: (control: ContextControl) => void;
};

export const AnalyticsContext = memo(function AnalyticsContext({
  panel,
  context: { field, values, options, label, operators },
  control,
  icon,
  onChange,
}: AnalyticsContextProps): JSX.Element | null {
  const { visualization } = options ?? {};

  const [draftControl, setDraftControl] = useState(control);

  // Some context filter might support selecting multiple values, but we don't enable it by default.
  // Instead we use the `isMultiple` flag to determine whether the user has enabled multi-selection.
  const supportsMultiple = visualization === 'multi-select';

  // This is true by default, when there are more than one selected values in the store.
  const [isMultiple, setIsMultiple] = useState(false);

  /** Whether the draft control is different than the control in the store. */
  const isDirty = useMemo(
    () => !isEqual(control, draftControl),
    [control, draftControl],
  );

  // Updates the `isMultiple` state depending on whether the context supports multiple selection.
  useEffect(() => {
    setIsMultiple(supportsMultiple && (control?.values?.length ?? 0) > 1);
  }, [control?.values?.length, supportsMultiple]);

  // Keeps the draft control in sync with the control in the store.
  useUpdateEffect(() => {
    if (isDirty && control) {
      setDraftControl(control);
    }
  }, [control]);

  /**
   * Handles toggling the isMultiple state.
   *
   * When toggling it off, we reset the selected values to the first if either
   * the selection has changed or includes more than one value.
   */
  const handleChangeIsMultiple = useCallback(
    (newIsMultiple: boolean) => {
      setIsMultiple(newIsMultiple);

      if (
        control &&
        !newIsMultiple &&
        ((draftControl && draftControl.values.length > 1) || isDirty)
      ) {
        onChange({
          ...control,
          values: draftControl?.values.slice(0, 1) ?? [],
        });
      }
    },
    [control, draftControl, isDirty, onChange],
  );

  /**
   * Handles updating the selected values.
   *
   * When selecting multiple values, we only update the internal `draftValues`
   * and then apply the change to the store when the dropdown is closed.
   *
   * When selecting a single value, the change is immediately applied and
   * reflected in the store.
   */
  const handleChange = useCallback<
    AnalyticsFilterChangeCallback<ContextControl>
  >(
    (payload) => {
      const updatedControl = produce(payload.filter, (draft) => {
        // When switching from the "is_not_empty" operator, which does not require any values
        // selected, to any other operator that requires values, we include the preselected values.
        draft.values = isOptionalOperator(control?.operator)
          ? (values
              .filter(({ preselected }) => preselected)
              .map(({ value }) => ({ value })) as DeepWritable<
              typeof draft.values
            >)
          : draft.values;
      });

      if (isMultiple) {
        setDraftControl(updatedControl);
        return;
      }

      if (!isEqual(updatedControl, control)) {
        onChange(updatedControl);
      }
    },
    [control, isMultiple, onChange, values],
  );

  /**
   * When the dropdown is closed, we check whether we are in multi-selection
   * mode and if any changes were made to the draft values and there is at least
   * one value selected, we update the store.
   */
  const handleToggle = useCallback(
    (isOpen: boolean) => {
      if (control && !isOpen && isMultiple && isDirty) {
        if (
          draftControl?.values.length ||
          isOptionalOperator(draftControl?.operator)
        ) {
          onChange({ ...control, ...draftControl });
        } else {
          setDraftControl(control);
        }
      }
    },
    [control, draftControl, isDirty, isMultiple, onChange],
  );

  // Don't render anything if there are no items to select, or if the context is configured to be
  // hidden, but with preselected items.
  if (!values.length || visualization === 'hidden') {
    return null;
  }

  // Render as plain text when only a single item is available.
  if (values.length === 1) {
    return (
      <FlexBox alignItems="center" gap="250">
        {icon && <Icon name={icon} color={typography.color['secondary']} />}

        <Text.Paragraph variant="1" subdued>
          {values[0].label}
        </Text.Paragraph>
      </FlexBox>
    );
  }

  const renderButtonText = (): string => {
    if (draftControl?.values.length) {
      const first = values.find(
        ({ value }) => value === draftControl.values[0].value,
      )?.label;

      const rest =
        draftControl.values.length > 1
          ? ` + ${draftControl.values.length - 1} more`
          : '';

      return `${
        draftControl.operator === 'is_not_one' ? 'All except ' : ''
      }${first} ${rest}`;
    }

    if (draftControl?.operator === 'is_not_empty') {
      return `All ${label?.toLowerCase()}s`;
    }

    return `Select ${label?.toLowerCase()}`;
  };

  return (
    <AnalyticsFilter<ContextControl>
      panel={panel}
      field={field}
      availableValues={values}
      selectedValues={draftControl?.values}
      pinnedValues={control?.values}
      availableOperators={operators}
      selectedOperator={draftControl?.operator}
      isMultiple={isMultiple}
      isClearable={false}
      isSelectionDisabled={isOptionalOperator(control?.operator)}
      hasDeterminateSelection={isMultiple}
      onChange={handleChange}
      onToggle={handleToggle}
      buttonProps={{
        variant: 'secondary',
        icon,
      }}
      {...(supportsMultiple && {
        customHeaderElement: (
          <ContextMultiToggle
            value={isMultiple}
            disabled={!draftControl?.values.length}
            onChange={handleChangeIsMultiple}
          />
        ),
      })}
    >
      {renderButtonText()}
    </AnalyticsFilter>
  );
});
